import React, {
    FunctionComponent,
    useState,
    ChangeEvent,
    useEffect,
} from 'react';
import { Step4ValueModel } from '../types';
import { useTranslation } from 'react-i18next';
import AgreementForm from './Forms/Agreement';
import SignatoryForm from './Forms/Signatory';
import ProductManagerForm from './Forms/ProductManager';
import SalesReportingForm from './Forms/SalesReporting';
import CombinedForm from './Forms/Combined';
import { validateStep4 } from '../Validation';
import { Step4Props } from './types';
import { FormErrorType } from "@sharedComponents/PantaForm/types";
import { getAllErrors } from "@sharedComponents/PantaForm/utils";
import Icon from "@sharedComponents/Icon";
import InputCheckbox from "@sharedComponents/InputCheckbox";
import Alert from "@sharedComponents/Alert";
import Button from "@sharedComponents/Button";

const Step4: FunctionComponent<Step4Props> = props => {
    const { t } = useTranslation()
    const [stepValues, setStepValues] = useState<Step4ValueModel>({})
    const [formErrors, setFormErrors] = useState<FormErrorType[]>([])
    const allErrors = getAllErrors(formErrors)

    const rehydrate = (values: any) => {
        if (values) {
            // Rehydrate form values when switching between steps,

            let sectionsFields: any = {
                agreement: {
                    firstname:
                        props.values.contactInfo?.agreement.firstname || '',
                    lastname:
                        props.values.contactInfo?.agreement.lastname || '',
                    email: props.values.contactInfo?.agreement.email || '',
                    mobileNumber:
                        props.values.contactInfo?.agreement.mobileNumber || '',
                    phoneNumber:
                        props.values.contactInfo?.agreement.phoneNumber || '',
                },
                signatory: {
                    firstname:
                        props.values.contactInfo?.signatory.firstname || '',
                    lastname:
                        props.values.contactInfo?.signatory.lastname || '',
                    email: props.values.contactInfo?.signatory.email || '',
                    mobileNumber:
                        props.values.contactInfo?.signatory.mobileNumber || '',
                    phoneNumber:
                        props.values.contactInfo?.signatory.phoneNumber || '',
                },
                productManager: {
                    firstname:
                        props.values.contactInfo?.productManager.firstname ||
                        '',
                    lastname:
                        props.values.contactInfo?.productManager.lastname || '',
                    email: props.values.contactInfo?.productManager.email || '',
                    mobileNumber:
                        props.values.contactInfo?.productManager.mobileNumber ||
                        '',
                    phoneNumber:
                        props.values.contactInfo?.productManager.phoneNumber ||
                        '',
                },
                salesReporting: {
                    firstname:
                        props.values.contactInfo?.salesReporting.firstname ||
                        '',
                    lastname:
                        props.values.contactInfo?.salesReporting.lastname || '',
                    email: props.values.contactInfo?.salesReporting.email || '',
                    mobileNumber:
                        props.values.contactInfo?.salesReporting.mobileNumber ||
                        '',
                    phoneNumber:
                        props.values.contactInfo?.salesReporting.phoneNumber ||
                        '',
                },
            }

            if (props.values.oneContact) {
                sectionsFields = {
                    combined: {
                        firstname:
                            props.values.contactInfo?.combined.firstname || '',
                        lastname:
                            props.values.contactInfo?.combined.lastname || '',
                        email: props.values.contactInfo?.combined.email || '',
                        mobileNumber:
                            props.values.contactInfo?.combined.mobileNumber ||
                            '',
                        phoneNumber:
                            props.values.contactInfo?.combined.phoneNumber ||
                            '',
                    },
                }
            }

            setStepValues({
                ...stepValues,
                oneContact: props.values.oneContact || false,
                contactInfo: sectionsFields,
            })
        }
    }

    useEffect(() => {
        // When rehydrate key toggles, regardless what value it has, reload values.
        rehydrate(props.values)
    }, [props.rehydrateFormKey])

    const nextButtonText = (
        <span className='buttons-wrapper__button-text buttons-wrapper__button-text--next'>
            <span>{t('global.preview')}</span>
        </span>
    )

    const clearSeparateContacts = () => {
        setStepValues({
            ...stepValues,
            contactInfo: {
                ...stepValues.contactInfo,
                agreement: {},
                signatory: {},
                productManager: {},
                salesReporting: {},
            },
        })
        props.onChangeValues &&
            props.onChangeValues({
                ...stepValues,
                contactInfo: {
                    ...stepValues.contactInfo,
                    agreement: {},
                    signatory: {},
                    productManager: {},
                    salesReporting: {},
                },
            })
    }

    const clearCombined = () => {
        setStepValues({
            ...stepValues,
            contactInfo: {
                ...stepValues.contactInfo,
                combined: {},
            },
        })
        props.onChangeValues &&
            props.onChangeValues({
                ...stepValues,
                contactInfo: {
                    ...stepValues.contactInfo,
                    combined: {},
                },
            })
    }

    const nextStep = () => {
        // Clear fields on non active view
        if (stepValues.oneContact) {
            clearSeparateContacts()
        } else {
            clearCombined()
        }

        validateStep4(stepValues, formErrors, stepValues.oneContact).then(
            (errors: FormErrorType[]) => {
                if (errors && errors.length > 0) {
                    setFormErrors(errors)
                } else {
                    setFormErrors([])
                    props.nextStep && props.nextStep()
                }
            }
        )
    }

    const prevStep = () => {
        props.prevStep && props.prevStep()
    }

    const onCancel = () => {
        setFormErrors([])
        props.onCancel && props.onCancel()
    }

    const onChangeOneContact = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.checked
        setFormErrors([])
        setStepValues({ ...stepValues, oneContact: value })
        props.onChangeValues && props.onChangeValues({ oneContact: value })
    }

    const cancelButtonText = (
        <span className='buttons-wrapper__button-text'>
            <Icon iconName="close" />
            <span>{t('button.cancel')}</span>
        </span>
    )

    const prevButtonText = (
        <span className='buttons-wrapper__button-text buttons-wrapper__button-text--prev'>
            <Icon iconName="arrow-left" />
            <span>{t('button.previous')}</span>
        </span>
    )

    return (
        <div className='producer-form form-step-wrapper form-step-wrapper--wide'>
            <h3>{t('companyRegistration.stepLabel.step4')}</h3>
            {props.cmsContent ? (
                <div className='producer-form__content'>
                    {props.cmsContent}
                </div>
            ) : (
                ''
            )}

            <div className='producer-form__combined'>
                <InputCheckbox
                    id="one-contact"
                    label={t('companyRegistration.combineAllContacts')}
                    onChange={onChangeOneContact}
                    checked={Boolean(stepValues.oneContact)}
                />
            </div>

            {!stepValues.oneContact ? (
                <>
                    <AgreementForm
                        stepValues={stepValues}
                        setStepValues={setStepValues}
                        formErrors={formErrors}
                        setFormErrors={setFormErrors}
                        onChangeValues={props.onChangeValues}
                    />
                    <SignatoryForm
                        stepValues={stepValues}
                        setStepValues={setStepValues}
                        formErrors={formErrors}
                        setFormErrors={setFormErrors}
                        onChangeValues={props.onChangeValues}
                    />
                    <ProductManagerForm
                        stepValues={stepValues}
                        setStepValues={setStepValues}
                        formErrors={formErrors}
                        setFormErrors={setFormErrors}
                        onChangeValues={props.onChangeValues}
                    />
                    <SalesReportingForm
                        stepValues={stepValues}
                        setStepValues={setStepValues}
                        formErrors={formErrors}
                        setFormErrors={setFormErrors}
                        onChangeValues={props.onChangeValues}
                    />
                </>
            ) : (
                <CombinedForm
                    stepValues={stepValues}
                    setStepValues={setStepValues}
                    formErrors={formErrors}
                    setFormErrors={setFormErrors}
                    onChangeValues={props.onChangeValues}
                />
            )}
            {allErrors.length > 0 && (
                <div className='producer-form__error'>
                    <Alert
                        type="danger"
                        text={t(
                            'companyRegistration.companyInfoContainsErrors'
                        )}
                        subtext={allErrors}
                        compact={false}
                    />
                </div>
            )}
            <div className='buttons-wrapper'>
                <Button
                    type="primary"
                    className="next-button"
                    text={nextButtonText}
                    onClick={nextStep}
                />
                <Button
                    type="primary"
                    className="prev-button"
                    text={prevButtonText}
                    onClick={prevStep}
                />
                <Button
                    type="outlined"
                    className="cancel-button"
                    text={cancelButtonText}
                    onClick={onCancel}
                />
            </div>
        </div>
    )
}

export default Step4;